import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { internalCatalogueApi } from '../../api/internal-catalogue-api';
import type { IInternalFieldDetail } from '../../api/internal-catalogue-contracts';
import { refusalMessage } from '../../refusal';
import { REASON_CLASSES, REINSTATEMENT_HANDLINGS } from '../../reinstatement';

/**
 * A Quality Rating's reasons and their lifecycle classes (SR-313; PRS v1.6 RNK-02, §9.2) — what
 * the reinstatement worklist reads to decide who comes back each cycle and how a reason's group is
 * handled. A score (a label that starts with a digit) has no class and is not listed. Saved through
 * survey-api's `option-classes`, by the codes as stored, so it serves the migrated reasons whose
 * Salesforce codes the values card cannot write.
 */

/** A kit `Select` needs a word for "nothing chosen" — not `SELECT_NONE`'s `none`, which is a
 *  handling's own value here. */
const UNSET = 'unset';

interface IClassRow {
  option: string;
  label: string;
  lifetime: string;
  handling: string;
}

function metaOf(meta: unknown): { lifetime?: string; handling?: string } {
  return meta !== null && typeof meta === 'object' ? (meta as { lifetime?: string; handling?: string }) : {};
}

export function isQualityRating(field: IInternalFieldDetail): boolean {
  return field.type === 'select' && field.code.endsWith('.quality_rating');
}

export function ReasonClasses({
  field,
  onChanged,
  onFailed
}: {
  field: IInternalFieldDetail;
  onChanged: () => void;
  onFailed: (message: string) => void;
}) {
  const [rows, setRows] = React.useState<IClassRow[]>(() =>
    field.options
      .filter((option) => !option.retired && !/^[0-9]/.test(option.label))
      .map((option) => {
        const meta = metaOf(option.meta);
        return {
          option: option.code,
          label: option.label,
          lifetime: meta.lifetime ?? UNSET,
          handling: meta.handling ?? UNSET
        };
      })
  );
  const [busy, setBusy] = React.useState(false);
  const edit = (index: number, change: Partial<IClassRow>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...change } : row)));

  const save = async () => {
    setBusy(true);
    try {
      await internalCatalogueApi.setOptionClasses(
        field.code,
        rows.map((row) => ({
          option: row.option,
          lifetime: row.lifetime === UNSET ? null : row.lifetime,
          handling: row.lifetime === UNSET || row.handling === UNSET ? null : row.handling
        }))
      );
      onChanged();
    } catch (error) {
      onFailed(refusalMessage(error, 'Could not save the reason classes'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-reason-classes="">
      <CardHeader>
        <CardTitle>Reason classes</CardTitle>
        <CardDescription>
          How long each reason keeps an advisor out, and how the reinstatement worklist handles its group. A legacy
          reason stays for reporting and cannot be chosen as a new value.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">This field holds no reasons.</p>}
        {rows.map((row, index) => (
          <div key={row.option} data-option={row.option} className="flex flex-wrap items-center gap-2">
            <span className="min-w-48 flex-1 text-sm">{row.label}</span>
            <ClassPicker
              label={`${row.label} class`}
              value={row.lifetime}
              unset="No class"
              items={REASON_CLASSES}
              className="w-64"
              onChange={(lifetime) => edit(index, { lifetime })}
            />
            <ClassPicker
              label={`${row.label} handling`}
              value={row.handling}
              unset="No handling"
              items={REINSTATEMENT_HANDLINGS}
              className="w-56"
              disabled={row.lifetime === UNSET}
              onChange={(handling) => edit(index, { handling })}
            />
          </div>
        ))}
        {rows.length > 0 && (
          <div className="flex">
            <Button className="ml-auto" disabled={busy} onClick={() => void save()}>
              Save classes
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ClassPicker({
  label,
  value,
  unset,
  items,
  className,
  disabled,
  onChange
}: {
  label: string;
  value: string;
  unset: string;
  items: readonly { value: string; label: string }[];
  className: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} disabled={disabled} onValueChange={onChange}>
      <SelectTrigger className={className} aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNSET}>{unset}</SelectItem>
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
